import React, { useCallback, useMemo, useState } from 'react';

import { SupportedServices } from '@ariksa/data-scanning/api';
import { SearchResponseLevel } from '@ariksa/inventory-core';
import {
  ContextType,
  DataSecurityIdentity,
  NativeResources,
} from '@ariksa/inventory-core/api';
import { NotificationFor } from '@ariksa/notification/api';
import {
  Box,
  Center,
  Flex,
  HStack,
  Stack,
  useDisclosure,
} from '@chakra-ui/react';
import { map, toLower, toUpper, uniq } from 'lodash';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { colorOpacity, customTheme } from 'theme';
import { Dict } from 'types/utils';

import {
  DataTypeTags,
  formatBytes,
  getIcon,
  getProviderName,
  renderRiskContext,
  renderStatusTag,
  renderTableHeaderWithLoader,
  renderTime,
  StackedCell,
  WithResourceIcon,
} from 'components/DataDisplay';
import { CustomTooltip } from 'components/DataDisplay/Tooltip/CustomTooltip';
import formatNumber from 'components/DataDisplay/Utils/formatNumber';
import { ActionButton } from 'components/DataEntry';
import {
  DataScanIcon,
  GraphIcon,
  IconTypes,
  ListIcon,
  NotifyIcon,
  SummaryIcon,
  TicketIcon,
} from 'components/Icons';
import { ContextLabelIcon } from 'components/Icons/ReactCustomIcons/ContextLabelIcon';
import { IMenuItemProps, TableActionsMenu } from 'components/Overlay';
import { useMetadataDrawerTabs } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/hooks/useMetadataDrawerTabs';
import { useActiveResourceContext } from 'containers/ActiveCloudResource/hooks/context/context';
import { useActiveResourceActions } from 'containers/ActiveCloudResource/hooks/useActiveResourceActions';
import { MetadataTabs } from 'containers/ActiveCloudResource/types';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import {
  ResourceTypeIconTooltip,
  StackedResourceType,
  useResourceType,
} from 'containers/App/hooks/useResourceType';
import { EnvironmentName } from 'containers/App/utils';
import { renderCloudIcons } from 'containers/Dashboard/utils/utils';
import { CreateNotification } from 'containers/Findings/Alerts/Components/CreateNotification';
import { NotificationResource } from 'containers/Findings/Alerts/types';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { selectSharedState } from 'containers/SharedState/selectors';
import {
  getDataTypeNameColor,
  getDataTypeLabels,
  renderDataTypeLabels,
  renderResources,
} from 'containers/Visibility/Data/Components/hooks/utils';
import { selectDataDashboard } from 'containers/Visibility/Data/selectors';
import { securityGraphRedirectUrl } from 'containers/Visibility/SecurityGraphNext/utils/securityGraphRedirectUrl';

import { useDataTypeClassifiers } from './useDataTypeClassifiers';

export const useDataTableColumns = (
  tabType,
  sensitiveRecordsByResource: Record<string, number> = {},
) => {
  const navigate = useNavigate();
  const { selectedTab } = useSelector(selectDataDashboard);
  const { riskContext } = useSelector(selectSharedState);
  const { onOpenMetadata } = useActiveResourceContext();
  const { updateActiveResource } = useActiveResourceActions();
  const { getTabIndex } = useMetadataDrawerTabs();
  const { getResourceAlias } = useResourceType();
  const ticketModal = useDisclosure();
  const notificationModal = useDisclosure();
  const {
    toCloudAccountName,
    accountMapping,
    renderStackedCloudAccountWithCloud,
    renderStackedAccountNameID,
  } = useCloudAccountId();
  const { environmentId } = useAccessBoundary();
  const { classifiers } = useDataTypeClassifiers();

  const getDataTypeClassifier = useCallback(
    dataType =>
      classifiers.find(
        classifier =>
          classifier.name === dataType || classifier.read_name === dataType,
      ),
    [classifiers],
  );

  const [currentRow, setCurrentRow] = useState<Dict<any> | null>(null);

  const handleRowClick = useCallback(
    (r, showAccessGraph = false) => {
      let resourceType: NativeResources = r?.native_name;
      if (r?.native_name === SupportedServices.GitLabBranch)
        resourceType = NativeResources.GitLabProject;
      else if (r?.native_name === SupportedServices.GitHubBranch)
        resourceType = NativeResources.GitHubRepository;
      else if (r?.native_name === SupportedServices.BitBucketBranch)
        resourceType = NativeResources.BitBucketRepository;

      updateActiveResource({
        resourceType,
        uuid: r?.extra?.repository_uuid ?? r?.id,
        accountId: r?.account,
        resourceId: r?.extra?.repository_resource_id ?? r?.resource_id,
      });
      onOpenMetadata(
        showAccessGraph
          ? getTabIndex(resourceType, tabType) ?? 0
          : getTabIndex(resourceType, MetadataTabs.Data),
      );
    },
    [onOpenMetadata, updateActiveResource, getTabIndex, tabType],
  );

  const name = {
    header: <Box pl="thLeftPaddingWithIcon">Name</Box>,
    render: ({ row }) => {
      const type = row.native_resource_type ?? row?.native_name;
      return (
        <WithResourceIcon
          resourceType={type}
          iconSize="sm"
          iconTooltip={<ResourceTypeIconTooltip resourceType={type} />}
        >
          <StackedCell
            upper={
              row?.extra_config?.source_name ||
              row.name ||
              row.resource_id ||
              row.uuid
            }
            lower={getResourceAlias(type)}
          />
        </WithResourceIcon>
      );
    },
  };

  const resources = {
    header: 'Sensitive Records',
    render: renderResources,
    align: 'center',
    //styles: { width: '60px', cell: { maxWidth: '60px', textAlign: 'right' } },
  };

  const owner = {
    header: 'Owner',
    accessor: 'extra_config.source_owner',
    render: ({ value }) => {
      if (!value) return '-';
      const val = !value ? '-' : value?.replace('+', ', ');
      return <CustomTooltip label={val}>{val}</CustomTooltip>;
    },
    align: 'left',
    //styles: { width: '100px', cell: { maxWidth: '100px' } },
  };

  const cloud = {
    header: 'Cloud',
    //align: 'left',
    render: ({ row }) => {
      const account = accountMapping[row?.account];
      return (
        <StackedCell
          upper={
            <Center w="full">
              <CustomTooltip
                label={getProviderName(
                  toLower(account?.cloud_type || row?.cloud),
                )}
                alignItems="center"
              >
                <Center boxSize={4}>
                  {getIcon(toLower(account?.cloud_type || row?.cloud))}
                </Center>
              </CustomTooltip>
            </Center>
          }
          lower={
            <CustomTooltip
              label={
                <Stack>
                  <HStack>
                    <Box color="orange">Name: </Box>
                    <Box>{account?.name}</Box>
                  </HStack>
                  <HStack>
                    <Box color="orange">Account ID: </Box>
                    <Box>{account?.cloud_account_id}</Box>
                  </HStack>
                </Stack>
              }
            >
              {account?.name}
            </CustomTooltip>
          }
          showUpperTooltip={false}
          showLowerTooltip={false}
        />
      );
    },
  };

  const sensitiveData = {
    header: <Box pl="thLeftPaddingWithIcon">Data Type</Box>,
    accessor: 'sensitive_data_name',
    render: ({ value, row }) => {
      const classifier = getDataTypeClassifier(value);
      const labels = getDataTypeLabels(classifier).length
        ? getDataTypeLabels(classifier)
        : getDataTypeLabels(row);
      const sensitivityCategory =
        classifier?.sensitivity_category ?? row?.sensitivity_category;
      const tooltipDetails = [...labels, sensitivityCategory]
        .filter(Boolean)
        .join(' / ');

      return (
        <WithResourceIcon
          resourceType={row.name}
          color={getDataTypeNameColor(classifier ?? row)}
          tooltip={
            <Stack spacing={0}>
              <Center color="orange">
                {classifier?.read_name ?? row?.sensitive_data_name ?? value}
              </Center>
              {tooltipDetails && <Center>{tooltipDetails}</Center>}
            </Stack>
          }
        >
          {value}
        </WithResourceIcon>
      );
    },
  };

  const sensitivity = {
    header: 'Sensitivity',
    accessor: 'sensitivity_category',
    // render: ({ row }) => (
    //   <StackedCell
    //     upper={row?.sensitivity_category}
    //     lower={renderSeverityBubble({ value: row?.sensitivity_level })}
    //     upperTooltip={false}
    //     lowerTooltip={false}
    //   />
    // ),
    align: 'left',
    styles: { width: '150px', cell: { maxWidth: '150px' } },
  };

  const dataTypeCount = {
    header: <Box pr={4}>Count</Box>,
    align: 'right',
    accessor: 'number_of_occurrences',
    render: ({ row }) => (
      <Box pr={4}>
        {formatNumber(row?.number_of_occurrences ?? row?.occurrences ?? 0)}
      </Box>
    ),
    styles: {
      width: '100px',
      cell: { maxWidth: '100px', textAlign: 'right' },
    },
  };

  const noOfOccurrences = {
    header: 'Occurrences',
    accessor: 'occurrences',
    render: ({ row }) => (
      <Box pr={10}>{formatNumber(row?.occurrences ?? 0)}</Box>
    ),
    styles: { width: '60px', cell: { maxWidth: '60px', textAlign: 'right' } },
  };

  const dataTypeClouds = {
    header: 'Cloud',
    // Centered to line up under the header; iconSize can be passed here too.
    render: ({ row }) =>
      renderCloudIcons(Object.keys(row?.clouds ?? {}), { justify: 'center' }),
  };

  const dataTypeSources = {
    header: 'Data Sources',
    accessor: 'sources',
    render: ({ value }) => formatNumber(value ?? 0),
    //align: 'left',
  };

  const renderActions = useCallback(
    ({ row }) => {
      const commonMenuItems = [
        {
          label: 'Create ticket',
          icon: <TicketIcon />,
          onClick: e => {
            e.stopPropagation();
            setCurrentRow(row);
            ticketModal.onOpen();
          },
        },
        {
          label: 'Send notification',
          icon: <NotifyIcon />,
          onClick: e => {
            e.stopPropagation();
            setCurrentRow(row);
            notificationModal.onOpen();
          },
        },
      ];
      let menuItems: IMenuItemProps[] = [];

      if (selectedTab === IconTypes.DataSources) {
        menuItems = [
          {
            label: 'Show security graph',
            icon: <GraphIcon />,
            onClick: e => {
              const { native_name, account, resource_id, id } = row;
              const url = securityGraphRedirectUrl({
                account_id: account,
                source_id: resource_id,
                resource_id: resource_id,
                resource_type: getDataResourceType(native_name),
                source_resource_uuid: id,
                map_type: 'identity',
                source_tab: MetadataTabs.Data,
                response_level: SearchResponseLevel.Compact,
              });
              navigate(url, { state: row });
              e.stopPropagation();
            },
          },
          {
            label: 'Show details',
            icon: <SummaryIcon />,
            onClick: e => {
              e.stopPropagation();
              handleRowClick(row, false);
            },
          },
          //...commonMenuItems,
          {
            label: 'Edit context',
            icon: <ContextLabelIcon />,
            onClick: e => {
              e.stopPropagation();
              handleRowClick(row, false);
            },
          },
          {
            label: 'Enable classification',
            icon: <DataScanIcon />,
            isDisabled: true,
            onClick: e => {
              e.stopPropagation();
              //handleRowClick(row, false);
            },
          },
        ];
      } else {
        menuItems = [
          {
            label: 'Show all sources',
            icon: <ListIcon />,
            onClick: e => {
              e.stopPropagation();
              // handleRowClick(row, true);
            },
          },
          //...commonMenuItems,
        ];
      }

      return (
        <TableActionsMenu menuItems={menuItems} styles={{ item: { h: 8 } }} />
      );
    },
    [handleRowClick, notificationModal, selectedTab, ticketModal, navigate],
  );

  const actions = {
    header: 'Action',
    render: renderActions,
    styles: { width: '60px', cell: { maxWidth: '60px' } },
  };

  const renderRiskContextTooltip = (label, values) => (
    <HStack spacing={2}>
      <Box>{label}:</Box>
      <Flex color="orange">{values?.join(', ')}</Flex>
    </HStack>
  );

  const context = {
    header: renderTableHeaderWithLoader('Risk Context', riskContext.isLoading),
    align: 'left',
    render: ({ row }) => {
      return renderRiskContext(riskContext?.data?.[row?.id], {
        [ContextType.SensitiveData]: (
          <Stack>
            <Box>{ContextType.SensitiveData}</Box>
            {renderRiskContextTooltip(
              'Categories',
              row?.sensitivity_categories,
            )}
            {renderRiskContextTooltip(
              'Labels',
              row?.source_labels ?? row?.labels,
            )}
          </Stack>
        ),
      });
    },
    //styles: { width: '100px', cell: { maxWidth: '100px' } },
  };

  const labels = {
    header: 'Labels',
    render: renderDataTypeLabels,
    align: 'left',
  };

  const sensitiveRecords = {
    header: 'Sensitive Records',
    render: ({ row }) => {
      const count = sensitiveRecordsByResource[row?.uuid];
      return count == null ? '-' : formatNumber(count, 1);
    },
    align: 'center',
  };

  const deployedInRegionAndAccount = {
    header: 'Deployed In',
    accessor: 'region',
    align: 'left',
    render: ({ row }) => (
      <StackedCell
        upper={renderStackedCloudAccountWithCloud(
          row?.account,
          true,
          row?.cloud,
        )}
        lower={row?.region}
        showUpperTooltip={false}
      />
    ),
  };

  const dataSources = [
    name,
    //dataSourcesSensitiveData,
    deployedInRegionAndAccount,
    resources,
    owner,
    context,
    labels,
    actions,
  ];

  const dataTypes = [
    sensitiveData,
    dataTypeClouds,
    {
      header: 'Regions',
      render: ({ row }) => row?.source_regions?.length ?? 0,
    },
    dataTypeSources,
    labels,
    dataTypeCount,
    sensitivity,
  ];

  const commonDocumentTypeColumns = [
    {
      header: 'Count',
      accessor: 'count',
      render: ({ value }) => formatNumber(value),
    },
    {
      header: 'Cloud',
      accessor: 'clouds',
      align: 'left',
      render: ({ value }) => renderCloudIcons(value),
    },
    {
      header: 'Sources',
      accessor: 'sources',
      render: ({ value }) => formatNumber(value),
    },
    {
      header: 'Found In',
      accessor: 'accounts',
      render: ({ value }) => value + ' account' + (value === 1 ? '' : 's'),
      align: 'left',
    },
    {
      header: 'Regions',
      accessor: 'regions',
      render: ({ value }) => value + ' region' + (value === 1 ? '' : 's'),
      align: 'left',
    },
    {
      header: 'Data Labels',
      accessor: 'labels',
      render: renderDataTypeLabels,
      align: 'left',
    },
  ];
  const documentTypes = [
    {
      header: <Box pl="thLeftPaddingWithIcon">Type</Box>,
      accessor: 'type_name',
      render: ({ value }) => (
        <WithResourceIcon
          resourceType={IconTypes.Policy}
          color={customTheme.colors.skyBlue['300']}
          useCustomColor
        >
          {value}
        </WithResourceIcon>
      ),
    },
    ...commonDocumentTypeColumns,
  ];
  const documentSubTypes = [
    {
      header: <Box pl="thLeftPaddingWithIcon">Sub-Type</Box>,
      accessor: 'subtype_name',
      render: ({ value }) => (
        <WithResourceIcon
          resourceType={IconTypes.Policy}
          color={colorOpacity(customTheme.colors.primary, 0.4)}
          useCustomColor
        >
          {value}
        </WithResourceIcon>
      ),
    },
    ...commonDocumentTypeColumns,
  ];
  const dataTypeSearchResultColumns = [
    { header: 'Data', accessor: 'pii_data' },
    cloud,
    {
      header: 'Environment',
      render: ({ row }) => (
        <EnvironmentName environmentId={environmentId ?? ''} />
      ),
      align: 'left',
    },
    {
      header: 'Resource',
      align: 'left',
      render: ({ row }) => (
        <StackedCell
          upper={row.resource}
          lower={getResourceAlias(row?.resource_type)}
        />
      ),
    },
    { header: 'Occurrence', accessor: 'occurrences' },
    { header: 'Region', accessor: 'region' },
  ];
  const dataTypeSourcesColumns = [
    {
      header: <Box pl="thLeftPaddingWithIcon">Name</Box>,
      render: ({ row }) => (
        <WithResourceIcon
          resourceType={row?.native_name}
          iconSize="sm"
          iconTooltip={
            <ResourceTypeIconTooltip resourceType={row?.native_name} />
          }
        >
          <StackedCell
            upper={row?.source ?? row?.resource_id}
            lower={getResourceAlias(row?.native_name)}
          />
        </WithResourceIcon>
      ),
    },
    {
      header: 'Account',
      accessor: 'account',
      align: 'left',
      render: ({ value }) => renderStackedAccountNameID(value),
      //styles: { width: '180px', cell: { maxWidth: '180px' } },
    },
    {
      header: 'Deployed In',
      accessor: 'region',
      align: 'left',
      //styles: { width: '60px', cell: { maxWidth: '60px' } },
    },
    noOfOccurrences,
    owner,
    context,
    labels,
    {
      header: 'Action',
      render: ({ row }) => (
        <Center>
          {/*<ActionButton
            label="Create ticket to remediate"
            icon={<TicketIcon />}
            onClick={() => {
              setCurrentRow(row);
              ticketModal.onOpen();
            }}
          />*/}
          <ActionButton
            label="Send notification"
            icon={<NotifyIcon />}
            onClick={() => {
              setCurrentRow(row);
              notificationModal.onOpen();
            }}
          />
        </Center>
      ),
      styles: { width: '60px', cell: { maxWidth: '60px' } },
    },
  ];
  const dataSourcesByDocTypeColumns = [
    {
      header: <Box pl="thLeftPaddingWithIcon">Data Source</Box>,
      render: ({ row }) => (
        <WithResourceIcon
          resourceType={row?.source_type}
          iconSize="sm"
          iconTooltip={
            <ResourceTypeIconTooltip resourceType={row?.source_type} />
          }
        >
          <StackedCell
            upper={row?.source ?? row?.resource_id}
            lower={getResourceAlias(row?.source_type)}
          />
        </WithResourceIcon>
      ),
    },
    {
      header: 'Count',
      accessor: 'count',
      render: ({ value }) => formatNumber(value),
    },
    {
      header: 'Cloud',
      accessor: 'cloud',
      align: 'left',
      render: ({ value }) => renderCloudIcons([value]),
    },
    {
      header: 'Deployed In',
      accessor: 'region',
      align: 'left',
      render: ({ row }) => (
        <StackedCell
          upper={toCloudAccountName(row?.account)}
          lower={row?.region}
        />
      ),
      //styles: { width: '60px', cell: { maxWidth: '60px' } },
    },
    labels,
    owner,
    {
      header: 'Action',
      render: ({ row }) => (
        <Center>
          {/*<ActionButton
            label="Create ticket to remediate"
            icon={<TicketIcon />}
            onClick={() => {
              setCurrentRow(row);
              ticketModal.onOpen();
            }}
          />*/}
          <ActionButton
            label="Send notification"
            icon={<NotifyIcon />}
            onClick={() => {
              setCurrentRow(row);
              notificationModal.onOpen();
            }}
          />
        </Center>
      ),
      styles: { width: '60px', cell: { maxWidth: '60px' } },
    },
  ];
  const dataSourcesByResourceTypeColumns = [
    name,
    deployedInRegionAndAccount,
    resources,
    {
      header: 'Scan Status',
      accessor: 'scan_status',
      render: ({ value }) => renderStatusTag(toUpper(value)),
    },
    owner,
    labels,
  ];
  const allDataSourcesColumns = [
    {
      header: <Box pl="thLeftPaddingWithIcon">Resource Type</Box>,
      render: ({ row }) => (
        <StackedResourceType resourceType={row?.native_resource} />
      ),
    },
    {
      header: 'Cloud',
      accessor: 'account_ids',
      render: ({ value }) => {
        return renderCloudIcons(
          uniq(map(value, o => accountMapping[o]?.cloud_type)),
        );
      },
      align: 'left',
    },
    {
      header: 'Total Sources',
      accessor: 'total',
    },
    {
      header: 'Accounts',
      accessor: 'account_ids',
      render: ({ value }) =>
        value?.length + ' account' + (value?.length === 1 ? '' : 's'),
      align: 'left',
    },
    {
      header: 'Regions',
      accessor: 'regions',
      render: ({ value }) =>
        value?.length + ' region' + (value?.length === 1 ? '' : 's'),
      align: 'left',
    },
  ];

  const fileColumns = [
    {
      header: 'filename path',
      accessor: 'file_full_path',
      render: ({ value }) => (
        <WithResourceIcon resourceType="Policy">{value}</WithResourceIcon>
      ),
    },
    {
      header: 'size',
      accessor: 'file_size',
      render: ({ value }) => formatBytes(value),
      align: 'left',
    },
    {
      header: 'Owner',
      accessor: 'source_owner',
      align: 'left',
    },
    {
      header: 'Last Update',
      accessor: 'last_modified',
      render: renderTime,
      align: 'left',
    },
  ];

  const openIdentity = useCallback(
    (row: DataSecurityIdentity) => {
      if (!row.native_resource_type) return;
      updateActiveResource({
        resourceType: row.native_resource_type as any,
        uuid: row.uuid,
        accountId: row.account_id!,
        resourceId: row.resource_id!,
        cloudProvider: row.cloud!,
      });
      onOpenMetadata(
        getTabIndex(row.native_resource_type as any, MetadataTabs.Data) ?? 0,
      );
    },
    [getTabIndex, onOpenMetadata, updateActiveResource],
  );

  const identityColumns = [
    name,
    {
      header: 'Account',
      accessor: 'account_id',
      align: 'left',
      render: ({ row }) => renderStackedAccountNameID(row.account_id || ''),
      styles: { width: '10%', cell: { maxWidth: '10%' } },
    },
    {
      header: 'Data Sources',
      accessor: 'data_source_count',
      render: ({ value }) => formatNumber(value ?? 0),
      align: 'center',
      //styles: { width: '8%', cell: { maxWidth: '8%' } },
    },
    labels,
    {
      header: 'Sensitive Data',
      render: ({ row }) => (
        <DataTypeTags
          dataTypes={row.data_types || []}
          classifiers={classifiers}
        />
      ),
      align: 'left',
      // Reserve width so the added Sensitive Records column can't squeeze the
      // tags onto extra lines.
      styles: { width: '24%', cell: { minWidth: '240px' } },
    },
    // Per-identity totals come from useSensitiveRecordsByResource, which
    // DataTableComponent only loads on the Identities tab.
    sensitiveRecords,
    {
      header: renderTableHeaderWithLoader(
        'Risk Context',
        riskContext.isLoading,
      ),
      render: ({ row }) => renderRiskContext(riskContext.data?.[row.uuid]),
      align: 'left',
      styles: { width: '120px', cell: { maxWidth: '120px' } },
    },
    {
      header: 'Actions',
      render: ({ row }) => {
        const menuItems: IMenuItemProps[] = [
          {
            label: 'Show details',
            icon: <SummaryIcon />,
            onClick: event => {
              event.stopPropagation();
              openIdentity(row);
            },
          },
        ];
        return <TableActionsMenu menuItems={menuItems} />;
      },
    },
  ];

  let tableColumns =
    selectedTab === IconTypes.DataSources
      ? dataSources
      : selectedTab === 'DocumentTypes'
      ? documentTypes
      : selectedTab === 'all'
      ? allDataSourcesColumns
      : selectedTab === 'Identities'
      ? identityColumns
      : dataTypes;

  const getDataResourceType = useCallback(
    (native_name?: string) => {
      const type =
        native_name ??
        currentRow?.service ??
        currentRow?.native_name ??
        currentRow?.source_type;
      switch (type) {
        case SupportedServices.GitLabBranch:
          return NativeResources.GitLabProject;
        case SupportedServices.BitBucketBranch:
          return NativeResources.BitBucketRepository;
        case SupportedServices.GitHubBranch:
          return NativeResources.GitHubRepository;
        default:
          return type;
      }
    },
    [currentRow],
  );

  const tableActionModals = useMemo(() => {
    const alert: NotificationResource = {
      entity_uuid: currentRow?.id ?? currentRow?.source_id,
      entity_id: currentRow?.resource_id,
      entity_type: getDataResourceType(),
      account_id: currentRow?.account_id ?? currentRow?.account,
      resource: getDataResourceType(),
      entity_name: currentRow?.source_name ?? currentRow?.source,
      environment_id: environmentId!,
    };

    return (
      <>
        {/*{currentRow && (
          <CreateTicket
            onClose={ticketModal.onClose}
            isOpen={ticketModal.isOpen}
            alert={alert}
            notification_for={NotificationFor.Data}
          />
        )}*/}
        {currentRow && (
          <CreateNotification
            onClose={notificationModal.onClose}
            isOpen={notificationModal.isOpen}
            alert={alert}
            notification_for={NotificationFor.Data}
          />
        )}
      </>
    );
  }, [
    currentRow,
    notificationModal.isOpen,
    notificationModal.onClose,
    //ticketModal.isOpen,
    //ticketModal.onClose,
    getDataResourceType,
    environmentId,
  ]);

  return {
    tableColumns,
    handleRowClick,
    tableActionModals,
    dataSourcesColumns: dataSources,
    dataTypeSourcesColumns,
    dataTypeSearchResultColumns,
    fileColumns,
    documentTypes,
    documentSubTypeColumns: documentSubTypes,
    dataSourcesByDocTypeColumns,
    dataSourcesByResourceTypeColumns,
    openIdentity,
  };
};
